import type { StateContext, Store } from '@ngxs/store';
import { Control, type DynamicFlatNode, type IChannel, isSameControlAsId, type ZoomSliderMode } from 'commons';
import type { SeriesOption } from 'echarts';
import { produce } from 'immer';
import { catchError, tap, withLatestFrom } from 'rxjs';
import type { ChannelInfoService } from '../../../../components/channel-color-dot/channel-info.service';
import type { SeriesD } from '../../../../components/dtis-multilane-chart/types/chart.types';
import type { TelemetryApiService } from '../../../../services/api/telemetry.service';
import { addToIndexedDB } from '../../../../state_utils';
import { TreeState } from '../../tree/TreeState';
import { type ExportFile, ExportFileFAILURE, ExportFileSUCCESS, GetBatchChannelData, GetChannelComments, type GetChannelData } from '../actions';
import { getExportTimeRange } from '../functions/export-time-range';
import { getTimeBoundaries } from '../functions/getTimeBoundaries';
import { disableLoading, enableLoading } from '../functions/toggleLoading';
import type { MultiViewStateModel } from '../MultiViewStateModel';
import { getCurrentView, getViewIndex } from '.';
import {
  checkAndFetchMissingData,
  getControlsForExport,
  handleDateRangeUpdate,
  processBatchResponse,
  processExportData,
  validateAndExtractChannels,
} from './data-utils';
import { setZoomSliderMode as setZoomSliderModeUtil } from './zoom-utils';

export namespace DataActionHandler {
  /**
   * Single channel data handler
   */
  export function getChannelDataH(
    ctx: StateContext<MultiViewStateModel>,
    { id, options, parameter: { replaceDate } }: GetChannelData,
    store: Store,
    telemetryService: TelemetryApiService
  ) {
    const channel_node = store.selectSnapshot(TreeState.getFlatTree).find(isSameControlAsId(id));

    if (!channel_node) {
      return console.warn("Channel doesn't exist");
    }

    const current_view_index = getViewIndex(ctx);
    const time = getTimeBoundaries(ctx, current_view_index, channel_node as DynamicFlatNode<IChannel>);

    enableLoading(ctx, current_view_index, id);

    return telemetryService
      .getChannelsData({
        channelIdentifiers: [id],
        ...time,
        ...options,
      })
      .pipe(
        catchError(() => {
          disableLoading(ctx, current_view_index, id);
          return [];
        }),
        tap(addToIndexedDB(id)),
        tap(() => ctx.dispatch(new GetChannelComments(id))),
        tap(() => disableLoading(ctx, current_view_index, id)),
        tap(({ startDateValues, endDateValues }) => handleDateRangeUpdate(ctx, startDateValues, endDateValues, replaceDate))
      );
  }

  /**
   * Batch channel data handler
   */
  export function getBatchChannelDataH(
    ctx: StateContext<MultiViewStateModel>,
    { channelIds, options, parameter: { replaceDate } }: GetBatchChannelData,
    telemetryService: TelemetryApiService,
    channelsInfoSevice: ChannelInfoService
  ) {
    const current_view_index = getViewIndex(ctx);
    const channelinfo = channelsInfoSevice.channelInfo();

    const { validChannelIds, validChannelMap, channelNodes } = validateAndExtractChannels(channelIds, channelinfo);

    if (validChannelIds.length === 0) {
      return console.warn('No valid channels to fetch');
    }

    const time = getTimeBoundaries(ctx, current_view_index, channelNodes[0]);

    // Enable loading for all channels
    validChannelIds.forEach((id) => {
      enableLoading(ctx, current_view_index, id);
    });

    return telemetryService
      .getChannelsData({
        channelIdentifiers: validChannelIds,
        ...time,
        ...options,
      })
      .pipe(
        catchError(() => {
          validChannelIds.forEach((id) => {
            disableLoading(ctx, current_view_index, id);
          });
          return [];
        }),
        tap((response) => processBatchResponse(ctx, response, validChannelMap, channelNodes, current_view_index)),
        tap(({ startDateValues, endDateValues }) => handleDateRangeUpdate(ctx, startDateValues, endDateValues, replaceDate))
      );
  }

  /**
   * Check missing channel data
   */
  export function checkMissingChannelData(ctx: StateContext<MultiViewStateModel>, store: Store) {
    const channels = ctx.getState().views[0].sensorTable.filter((c) => new Control(c).mappingString);

    return Promise.all(channels.map((channel) => checkAndFetchMissingData(channel, store)));
  }

  /**
   * Checks in the background whether newer telemetry data than what is currently rendered
   * is available on the backend, by requesting the data of a representative channel for the
   * time window since the last successful fetch. Sets `newDataAvailableSince` on the current
   * view when the backend reports a newer end date, without touching the rendered chart data.
   */
  export function checkForNewTelemetryDataH(ctx: StateContext<MultiViewStateModel>, telemetryService: TelemetryApiService) {
    const view = getCurrentView(ctx.getState());
    const channel = view?.sensorTable.find((c) => new Control(c).mappingString);

    if (!view || !channel) {
      return;
    }

    const since = view.lastTelemetryFetchedAt ? new Date(view.lastTelemetryFetchedAt) : new Date(view.dateRange.end_date);

    return telemetryService
      .getChannelsData({
        channelIdentifiers: [channel.id],
        startTime: since,
        endTime: new Date(),
        windowSize: channel.windowSize,
        windowUnit: channel.windowUnit,
      })
      .pipe(
        catchError(() => []),
        tap((response) => {
          if (!response?.endDateValues) {
            return;
          }
          const latest = new Date(response.endDateValues);
          if (latest.getTime() > since.getTime()) {
            ctx.setState(
              produce(ctx.getState(), (draft) => {
                const draftView = draft.views[getViewIndex(ctx)];
                if (draftView) {
                  draftView.newDataAvailableSince = latest;
                }
              })
            );
          }
        })
      );
  }

  /**
   * Fetches the newer telemetry data detected by `checkForNewTelemetryDataH`, replacing the
   * data stored in IndexedDB for every channel of the current view and re-rendering the chart.
   */
  export function loadNewTelemetryDataH(ctx: StateContext<MultiViewStateModel>, store: Store) {
    const view = getCurrentView(ctx.getState());
    const channelIds = view?.sensorTable.filter((c) => new Control(c).mappingString).map(({ id }) => id) ?? [];

    if (channelIds.length === 0) {
      return;
    }

    return store.dispatch(new GetBatchChannelData(channelIds, {}, { replaceDate: true })).pipe(
      tap(() => {
        ctx.setState(
          produce(ctx.getState(), (draft) => {
            const draftView = draft.views[getViewIndex(ctx)];
            if (draftView) {
              draftView.newDataAvailableSince = undefined;
            }
          })
        );
      })
    );
  }

  /**
   * Dismisses the "new data available" hint without fetching new data.
   */
  export function dismissNewTelemetryDataH(ctx: StateContext<MultiViewStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const draftView = draft.views[getViewIndex(ctx)];
        if (draftView) {
          draftView.newDataAvailableSince = undefined;
        }
      })
    );
  }

  /**
   * Export view data
   */
  export function exportViewAs(
    ctx: StateContext<MultiViewStateModel>,
    { payload: { filename, resolution, type, laneIds } }: ExportFile,
    store: Store,
    telemetryService: TelemetryApiService
  ) {
    const currentView = getCurrentView(ctx.getState());
    if (!currentView) {
      return;
    }

    const timeRange = getExportTimeRange(currentView.zoomState, currentView.lastZoomedId, currentView.dateRange);
    if (!timeRange) {
      return;
    }

    const controls = getControlsForExport(store, laneIds);

    return telemetryService
      .getChannelsData({
        channelIdentifiers: controls.map(({ id }) => id),
        startTime: timeRange.startTime,
        endTime: timeRange.endTime,
        windowUnit: resolution.windowUnit,
        windowSize: resolution.windowSize,
      })
      .pipe(
        withLatestFrom(store.selectOnce(TreeState.getFlatTree)),
        tap(([data, nodes]) => processExportData(data, nodes, type, filename)),
        tap(() => ctx.dispatch(new ExportFileSUCCESS())),
        catchError(() => ctx.dispatch(new ExportFileFAILURE(filename, type, resolution)))
      );
  }

  /**
   * Set zoom slider mode
   */
  export function setZoomSliderMode(ctx: StateContext<MultiViewStateModel>, zoomSliderMode: ZoomSliderMode) {
    setZoomSliderModeUtil(ctx, zoomSliderMode);
  }

  /**
   * Set date range by data
   */
  export function setDateRangeByDataH(ctx: StateContext<MultiViewStateModel>) {
    const view = getCurrentView(ctx.getState());

    const allSortedDates = (view?.echartsConfig.series as SeriesOption[])
      ?.flatMap((series) => (series?.data as SeriesD)?.map(([dateTime]) => new Date(dateTime)))
      .filter((d) => d !== undefined)
      .sort((a, b) => a.getTime() - b.getTime());

    if (allSortedDates?.length <= 0) {
      return;
    }

    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const end_date = allSortedDates.at(-1);
        if (!end_date) {
          console.warn('Could not determine end date from data');
          return;
        }
        draft.views[getViewIndex(ctx)].dateRange = {
          start_date: allSortedDates[0],
          end_date,
        };
      })
    );
  }
}
